/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable camelcase */
import { Box, Chip, Stack, Tooltip } from '@mui/material';

import { useEffect, useState } from 'react';

import { ITagsOptions } from '../api/type-def';
import { SYSTEM_TAGS } from '../PromptBuilderDetails/PromptBuilderConstants';

import tagStyles from './PromptTags.module.scss';

interface IPrompTag {
	promptData: any;
}

const PromptTags = (props: IPrompTag) => {
	const { promptData = {} } = props;
	const { systemTagColor, userTagColor, draftTagColor } = tagStyles;

	const { DEFAULT, DRAFT } = SYSTEM_TAGS;

	const MAX_VISIBLE_TAGS = 4;

	const [visibleTags, setVisibleTags] = useState<
		ITagsOptions[] | undefined | null
	>([]);
	const [additionalTags, setAdditionalTags] = useState<string>('');
	const [additionalTagsCount, setAdditionalTagsCount] = useState<number>(0);

	const { is_active = false, is_draft = false } = promptData;

	useEffect(() => {
		const tags = [...(promptData.tags || [])];

		if (is_active) {
			tags.push({ name: DEFAULT, is_default: true });
		}
		if (is_draft) {
			tags.push({ name: DRAFT, is_default: true });
		}

		// Extract locations from promptData
		if (promptData.locations && Array.isArray(promptData.locations)) {
			promptData.locations.forEach((location: string) => {
				tags.push({ name: location, is_default: true });
			});
		}

		// Extract industry and category from parameters
		if (promptData.parameters) {
			const { industry, category } = promptData.parameters;
			if (industry) {
				tags.push({ name: industry, is_default: true });
			}
			if (category) {
				tags.push({ name: category, is_default: true });
			}
		}

		// Sorting function based on the specified priority.
		tags.sort((a: ITagsOptions, b: ITagsOptions) => {
			// Priority order: Default, Draft, system-generated, user-generated
			const getPriority = (tag: ITagsOptions) => {
				if (tag.name === DEFAULT) return 1;
				if (tag.name === DRAFT) return 2;
				if (tag.is_default) return 3; // system-generated
				return 4; // user-generated
			};

			return getPriority(a) - getPriority(b);
		});

		// Determine visible and additional tags
		setVisibleTags(tags?.slice(0, MAX_VISIBLE_TAGS));
		setAdditionalTagsCount(tags?.length - MAX_VISIBLE_TAGS);
		setAdditionalTags(
			tags
				.slice(MAX_VISIBLE_TAGS)
				.map((tag: any) => tag.name)
				.join(', '),
		);
	}, [promptData]);

	// Function to determine background color
	const getBackgroundColor = (tag: any) => {
		if (tag.name === DRAFT) return draftTagColor;
		if (tag.is_default) return systemTagColor;
		return userTagColor;
	};

	return (
		<Stack direction="row" spacing={0.3}>
			{visibleTags?.map((tag: any, index: number) => (
				<Box key={tag?.name} className={tagStyles['chip-wrapper']}>
					{/* Chip rendering */}
					<Chip
						label={tag?.name}
						className={tagStyles['display-chip-style']}
						sx={{
							backgroundColor: getBackgroundColor(tag),
						}}
					/>
					{/* Render line between system-generated tags */}
					{index < visibleTags.length - 1 &&
						tag.is_default &&
						tag.name !== DRAFT &&
						visibleTags[index + 1]?.is_default &&
						visibleTags[index + 1]?.name !== DRAFT && (
							<Box
								sx={{
									position: 'absolute',
									top: '50%',
									right: '-8px',
									width: '16px',
									height: '4px',
									backgroundColor: systemTagColor, // Use the desired color for the line
									transform: 'translateY(-50%)',
								}}
							/>
						)}
				</Box>
			))}

			{/* Display additional tag count */}
			{additionalTagsCount > 0 && (
				<Tooltip title={additionalTags} placement="top" arrow>
					<Chip label={`+${additionalTagsCount}`} />
				</Tooltip>
			)}
		</Stack>
	);
};

export default PromptTags;
